import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api.js';

interface EvidenceItem {
  id: string;
  evidenceType: string;
  fileName: string;
  mimeType?: string | null;
  extractionStatus: string;
  extractedFields?: Record<string, { value: unknown; confidence: number }> | null;
  extractionConfidence?: string | null;
  isHandwritten: boolean;
  ocrText?: string | null;
}

interface ClaimEvidencePanelProps {
  evidence: EvidenceItem[];
  claimId: string;
}

const EXTRACTION_STATUS_COLORS: Record<string, string> = {
  COMPLETED: 'bg-success/10 text-success',
  FAILED: 'bg-danger/10 text-danger',
  PENDING: 'bg-slate-100 text-slate-500',
  PROCESSING: 'bg-copper-soft text-copper',
  QUEUED: 'bg-copper-soft text-copper-deep',
};

function FieldRow({
  label,
  value,
  confidence,
}: {
  label: string;
  value: unknown;
  confidence: number;
}) {
  const pct = Math.round(confidence * 100);
  const color = pct >= 80 ? 'text-success' : pct >= 50 ? 'text-copper' : 'text-danger';
  return (
    <div className="flex items-start gap-2 text-sm py-1 border-b border-hairline last:border-0">
      <span className="text-slate-500 w-36 shrink-0 text-xs mt-0.5">{label}</span>
      <span className="flex-1 font-mono text-xs break-all">
        {typeof value === 'object' ? JSON.stringify(value) : String(value ?? '—')}
      </span>
      <span className={['font-mono text-xs shrink-0', color].join(' ')}>{pct}%</span>
    </div>
  );
}

function EvidenceCard({ item, claimId }: { item: EvidenceItem; claimId: string }) {
  const { t } = useTranslation();
  const [reExtractState, setReExtractState] = useState<'idle' | 'loading' | 'queued' | 'error'>(
    'idle',
  );

  const confPct =
    item.extractionConfidence != null
      ? Math.round(Number(item.extractionConfidence) * 100)
      : null;

  const statusClass =
    EXTRACTION_STATUS_COLORS[item.extractionStatus] ?? 'bg-slate-100 text-slate-500';

  async function handleReExtract() {
    setReExtractState('loading');
    try {
      await api.post<unknown>(
        `/approver/claims/${claimId}/re-extract/${item.id}`,
        {},
      );
      setReExtractState('queued');
    } catch {
      setReExtractState('error');
    }
  }

  return (
    <details className="group border border-hairline rounded bg-bone overflow-hidden">
      <summary className="cursor-pointer list-none px-4 py-3 flex items-center gap-3 hover:bg-bone-soft transition-colors select-none">
        {/* Expand indicator */}
        <span className="text-slate-400 text-xs group-open:rotate-90 transition-transform">
          ▶
        </span>

        {/* File name */}
        <span className="font-medium text-sm text-ink flex-1 min-w-0 truncate">
          {item.fileName}
        </span>

        {/* Meta badges */}
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs px-2 py-0.5 rounded-sm bg-slate-100 text-slate-500 border border-hairline">
            {item.evidenceType}
          </span>
          <span className={['text-xs px-2 py-0.5 rounded-sm font-semibold', statusClass].join(' ')}>
            {item.extractionStatus}
          </span>
          {confPct != null && (
            <span className="font-mono text-xs text-slate-400">{confPct}%</span>
          )}
          {item.isHandwritten && (
            <span className="text-xs text-copper font-medium">
              {t('extraction.handwritten')}
            </span>
          )}
        </div>
      </summary>

      {/* Expanded content */}
      <div className="px-4 pb-4 pt-2 space-y-4 border-t border-hairline">
        {/* Extracted fields */}
        {item.extractedFields && Object.keys(item.extractedFields).length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
              {t('wizard.labels.extractedFields')}
            </p>
            <div className="bg-bone-soft border border-hairline rounded p-2">
              {Object.entries(item.extractedFields).map(([k, v]) => (
                <FieldRow
                  key={k}
                  label={k}
                  value={v?.value}
                  confidence={v?.confidence ?? 0}
                />
              ))}
            </div>
          </div>
        )}

        {/* OCR text collapsible */}
        {item.ocrText && (
          <details className="group/ocr">
            <summary className="cursor-pointer list-none text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-copper transition-colors select-none">
              <span className="group-open/ocr:hidden">▶</span>
              <span className="hidden group-open/ocr:inline">▼</span>
              {' '}
              {t('approver.evidence.ocrText', { defaultValue: 'OCR Text' })}
            </summary>
            <pre className="mt-2 text-xs font-mono text-slate-600 bg-bone-soft border border-hairline rounded p-3 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
              {item.ocrText}
            </pre>
          </details>
        )}

        {/* Re-extract button */}
        <div className="flex items-center gap-3">
          {reExtractState === 'queued' ? (
            <span className="text-xs text-success font-medium">
              ✓ {t('approver.evidence.queued', { defaultValue: 'Queued for re-extraction' })}
            </span>
          ) : reExtractState === 'error' ? (
            <span className="text-xs text-danger">
              {t('errors.internalServerError')}
            </span>
          ) : (
            <button
              type="button"
              disabled={reExtractState === 'loading'}
              onClick={() => { void handleReExtract(); }}
              className="px-3 py-1.5 text-xs font-semibold border border-hairline rounded text-slate-600 hover:border-copper hover:text-copper transition-colors disabled:opacity-50"
            >
              {reExtractState === 'loading'
                ? t('approver.evidence.reExtracting', { defaultValue: 'Queuing...' })
                : t('approver.evidence.reExtract', { defaultValue: 'Re-extract' })}
            </button>
          )}
        </div>
      </div>
    </details>
  );
}

export function ClaimEvidencePanel({ evidence, claimId }: ClaimEvidencePanelProps) {
  const { t } = useTranslation();

  if (evidence.length === 0) {
    return (
      <div className="py-4 text-center text-sm text-slate-400">
        {t('approver.evidence.none', { defaultValue: 'No evidence documents attached.' })}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {evidence.map((item) => (
        <EvidenceCard key={item.id} item={item} claimId={claimId} />
      ))}
    </div>
  );
}
